import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { ContactosService } from '../contactos.service';

/**
 * Service for managing contact official ID documents
 * Handles ID upload, tracking, and validation for different contact types
 * 
 * Category: Helper Service
 * Purpose: ID document management, file upload coordination, contact ID tracking
 */
@Injectable()
export class ContactIdManagerService {

  constructor(private contactosService: ContactosService) { }

  /**
   * Contact types enum for type safety
   */
  readonly CONTACT_TYPES = {
    USUARIO: 'Usuario',
    COORDINADOR: 'Coordinador',
    CONTRATANTE: 'Contratante'
  };

  /**
   * Gets the contact ID from service contacts object based on type
   * @param contactos - Service contactos object
   * @param contactType - Type of contact
   * @returns Contact ID string
   */
  getContactIdByType(contactos: any, contactType: string): string {
    if (!contactos) {
      return '';
    }

    switch (contactType) {
      case this.CONTACT_TYPES.USUARIO:
        return contactos.usuario || '';
      case this.CONTACT_TYPES.COORDINADOR:
        return contactos.coordinador || '';
      case this.CONTACT_TYPES.CONTRATANTE:
        return contactos.contratante || '';
      default:
        return '';
    }
  }

  /**
   * Checks if a contact has an official ID uploaded
   * @param contactId - Contact ID to check
   * @returns Observable with ID link or null
   */
  checkContactHasId(contactId: string): Observable<any> {
    if (!contactId) {
      return Observable.of(null);
    }
    
    return this.contactosService
      .obtenerPropiedadContacto(contactId, 'id_link')
      .valueChanges()
      .take(1);
  }

  /**
   * Uploads official ID for a contact
   * @param contactId - Contact ID
   * @param clienteId - Cliente ID
   * @param file - File to upload
   * @returns Promise with upload result
   */
  uploadContactId(contactId: string, clienteId: string, file: File): Promise<any> {
    return this.contactosService
      .subirIdOficial(contactId, file)
      .then(uploadResult => {
        return this.contactosService.actualizarIdContacto(
          contactId,
          clienteId,
          uploadResult.downloadURL
        );
      });
  }

  /**
   * Handles the complete ID upload flow for a contact
   * @param contactos - Service contactos object
   * @param contactType - Type of contact
   * @param clienteId - Cliente ID
   * @param file - File to upload
   * @returns Promise that resolves to the download URL
   */
  handleIdUpload(
    contactos: any,
    contactType: string,
    clienteId: string,
    file: File
  ): Promise<string> {
    const contactId = this.getContactIdByType(contactos, contactType);
    
    if (!contactId) {
      return Promise.reject('Contact ID not found');
    }

    return this.uploadContactId(contactId, clienteId, file)
      .then(result => {
        return result.downloadURL || result;
      });
  }

  /**
   * Gets ID status for all contact types in a service
   * @param contactos - Service contactos object
   * @returns Observable with all ID statuses as array [usuario, coordinador, contratante]
   */
  getAllContactIdStatuses(contactos: any): Observable<any[]> {
    const usuario$ = this.checkContactHasId(contactos.usuario);
    const coordinador$ = this.checkContactHasId(contactos.coordinador);
    const contratante$ = this.checkContactHasId(contactos.contratante);

    return Observable.forkJoin([usuario$, coordinador$, contratante$]);
  }

  /**
   * Validates if file is acceptable for ID upload
   * @param file - File to validate
   * @returns True if file is valid
   */
  validateIdFile(file: File): boolean {
    if (!file) {
      return false;
    }

    const maxSizeInMB = 10;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    
    const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

    return file.size <= maxSizeInBytes && acceptedTypes.includes(file.type);
  }

  /**
   * Gets display name for file upload button
   * @param file - File object or null
   * @param defaultText - Default text if no file
   * @returns Display name
   */
  getFileDisplayName(file: File | null, defaultText: string = 'Subir ID Oficial'): string {
    return file ? file.name : defaultText;
  }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/helpers/contact-id-manager.service.ts